import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarOff, CalendarClock, Users } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from './ui/card.js';
import { Pill } from './ui/pill.js';
import { Feedback } from './ui/feedback.js';
import { api, type FieldPhase, type SalesOrder, type Tech } from '../lib/api.js';
import {
  durationLabel,
  FOP_UDF,
  readAssignment,
  readCompletedAt,
  readHelpers,
  useDispatchBoard,
  windowLabel,
} from '../lib/dispatch-board.js';
import { offDuring, unavailability, useAvailabilityWindow, type Unavailable } from '../lib/availability.js';
import { phaseLabel, phasePillVariant } from '../lib/field-time.js';
import { tradeLabel, useSalesTypes } from '../lib/trade.js';
import { useAuth } from '../lib/auth-store.js';
import { JobVisits } from './job-visits.js';

/**
 * Office-side view of a job's dispatch state — what the board knows that the
 * Spire header doesn't: the scheduled day + time window, who's going (lead +
 * helpers, with their skills and whether they're actually available that
 * day), the field phase and when the lead clocked out. Read-only; changes
 * happen on the board (the link jumps there with the right day selected).
 */
export function DispatchCard({ order }: { order: SalesOrder }) {
  const udf = order.udf ?? {};
  const assignment = readAssignment(udf);
  const helperIds = readHelpers(udf);
  const completedAt = readCompletedAt(udf);
  const phase = readPhase(udf[FOP_UDF.phase]);
  // Full roster, inactive included — a job dispatched last month to someone
  // who's since left should still name them, not say "unknown".
  const techsQuery = useQuery({
    queryKey: ['admin', 'techs', { active: false }] as const,
    queryFn: ({ signal }) => api.admin.techs.list(true, signal),
    staleTime: 60_000,
  });
  const techs = techsQuery.data ?? [];
  const salesTypes = useSalesTypes();
  // Scheduling visits is an office job; techs see them on My Day.
  const role = useAuth((s) => s.user?.role);
  const canSchedule = role === 'admin' || role === 'dispatcher' || role === 'office';
  const setSelectedDate = useDispatchBoard((s) => s.setSelectedDate);
  const select = useDispatchBoard((s) => s.select);

  const date = assignment?.date ?? '';
  const avail = useAvailabilityWindow(date || '0000-00-00', date || '0000-00-00');
  const whyOff = (tech: Tech): Unavailable | null => {
    if (!date || !assignment) return null;
    const why = unavailability(tech, date, avail.timeOff.filter((o) => o.techId === tech.id), avail.holidays);
    // A morning off doesn't flag an afternoon job.
    return offDuring(why, assignment.startHour, assignment.duration) ? why : null;
  };

  // Invoiced / closed orders that were never dispatched have nothing to say;
  // ones that WERE keep their crew + completion history visible.
  if (!assignment && order.status !== 'open') return null;

  const lead = assignment ? techs.find((t) => t.id === assignment.techId) ?? null : null;
  const helpers = helperIds.map((id) => techs.find((t) => t.id === id) ?? null);

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle>Dispatch</CardTitle>
        {assignment && (
          <Link
            to="/operations"
            search={{ status: 'open', orderNo: order.orderNo }}
            onClick={() => {
              setSelectedDate(assignment.date);
              select(order.spireId);
            }}
            className="text-xs text-ink-accent hover:underline"
          >
            Open on board →
          </Link>
        )}
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        {!assignment ? (
          <div className="text-ink-muted">
            Not scheduled.{' '}
            <Link to="/operations" search={{ status: 'open', orderNo: order.orderNo }} className="text-ink-accent hover:underline">
              Drop it on the board
            </Link>{' '}
            to assign a tech and a time.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Date" value={longDate(assignment.date)} />
              <Field label="Window" value={windowLabel(assignment.startHour, assignment.duration)} />
              <Field label="Duration" value={durationLabel(assignment.duration)} />
              <Field
                label="Phase"
                value={
                  phase ? (
                    <Pill variant={phasePillVariant(phase)} dot>
                      {phaseLabel(phase)}
                    </Pill>
                  ) : (
                    <span className="text-ink-muted">Scheduled</span>
                  )
                }
              />
            </div>

            {completedAt && (
              <Feedback variant="success" size="sm">
                Completed {completedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} — the lead clocked out and marked the visit done.
              </Feedback>
            )}

            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-ink-subtle inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" aria-hidden /> Crew
              </div>
              <TechRow
                role="Lead"
                tech={lead}
                fallbackId={assignment.techId}
                off={lead ? whyOff(lead) : null}
                skills={lead ? lead.trades.map((t) => tradeLabel(salesTypes, t)).join(' · ') : ''}
                loading={techsQuery.isPending}
              />
              {helpers.map((h, i) => (
                <TechRow
                  key={helperIds[i] ?? i}
                  role="Helper"
                  tech={h}
                  fallbackId={helperIds[i] ?? '?'}
                  off={h ? whyOff(h) : null}
                  skills={h ? h.trades.map((t) => tradeLabel(salesTypes, t)).join(' · ') : ''}
                  loading={techsQuery.isPending}
                />
              ))}
              {helpers.length === 0 && <div className="text-xs text-ink-subtle pl-9">No helpers assigned.</div>}
            </div>

            {canSchedule && (
              <JobVisits
                spireId={order.spireId}
                techs={techs.filter((t) => t.active)}
                first={{
                  date: assignment.date,
                  startHour: assignment.startHour,
                  duration: assignment.duration,
                  techId: assignment.techId,
                }}
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <div className="text-xs uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="text-ink">{value}</div>
    </div>
  );
}

function TechRow({
  role,
  tech,
  fallbackId,
  off,
  skills,
  loading,
}: {
  role: 'Lead' | 'Helper';
  tech: Tech | null;
  fallbackId: string;
  off: Unavailable | null;
  skills: string;
  loading: boolean;
}) {
  const swatch = tech ? AVATAR_SWATCH[tech.avatar] : { bg: 'var(--color-background-tertiary)', fg: 'var(--color-text-secondary)' };
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
        style={{ backgroundColor: swatch.bg, color: swatch.fg }}
      >
        {tech?.initials ?? '?'}
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium text-ink">
            {tech?.name ?? (loading ? 'Loading…' : `Unknown tech (${fallbackId})`)}
          </span>
          <Pill variant="neutral">{role}</Pill>
          {tech && !tech.active && <Pill variant="warning">Inactive</Pill>}
        </div>
        <div className="text-xs text-ink-muted truncate">
          {off ? (
            <span className="inline-flex items-center gap-1 font-medium text-amber-700">
              <CalendarOff className="h-3 w-3" aria-hidden />
              {off.label} — scheduled anyway
            </span>
          ) : (
            skills || (tech ? 'No skills set' : 'Not on the current roster — reassign on the board')
          )}
        </div>
      </div>
      {!off && tech && (
        <CalendarClock className="h-4 w-4 text-ink-subtle shrink-0" aria-label="Available that day" />
      )}
    </div>
  );
}

/** Same swatches as the board's tech cell so the same person reads the same everywhere. */
const AVATAR_SWATCH: Record<Tech['avatar'], { bg: string; fg: string }> = {
  install: { bg: '#CECBF6', fg: '#26215C' },
  service: { bg: '#B5D4F4', fg: '#042C53' },
  pm: { bg: '#9FE1CB', fg: '#04342C' },
  emergency: { bg: '#F7C1C1', fg: '#501313' },
  sales: { bg: '#FAC775', fg: '#412402' },
};

function readPhase(value: string | undefined): FieldPhase | null {
  switch (value) {
    case 'scheduled':
    case 'traveling':
    case 'on_site':
    case 'work_in_progress':
    case 'paused':
    case 'work_completed':
    case 'signed_off':
      return value;
    default:
      return null;
  }
}

function longDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
